import React, { useState } from 'react';
import {
  SERVICE_TYPES,
  getWMSLayers, buildWMSTileUrl,
  getWFSFeatureTypes, fetchWFSFeatures,
  getWMTSLayers,
  buildArcGISTileUrl, fetchArcGISFeatures,
  queryOverpass,
} from '../lib/ogc-services';
import { useTheme } from '../contexts/ThemeContext';

/**
 * AddServiceDialog — modal for connecting external map services
 *
 * Props:
 *   onAddRasterLayer(name, tileUrl) — add raster tile source to map
 *   onAddVectorLayer(name, geojson) — add GeoJSON vector source to map
 *   onClose() — close dialog
 *   initialType — service tab to open on (xyz, wms, wfs, wmts, arcgis, osm)
 *   getBbox() — current map bbox [west, south, east, north] for Overpass {{bbox}}
 */
export default function AddServiceDialog({ onAddRasterLayer, onAddVectorLayer, onClose, initialType = 'xyz', getBbox }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [activeType, setActiveType] = useState(SERVICE_TYPES.some(t => t.id === initialType) ? initialType : 'xyz');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableLayers, setAvailableLayers] = useState([]);
  const [selectedLayer, setSelectedLayer] = useState('');
  const [overpassQuery, setOverpassQuery] = useState('node["amenity"="hospital"]({{bbox}});');
  const [layerName, setLayerName] = useState('');

  const reset = () => { setAvailableLayers([]); setSelectedLayer(''); setError(null); };

  const handleConnect = async () => {
    setError(null);
    setLoading(true);
    try {
      switch (activeType) {
        case 'xyz': {
          if (!url) throw new Error('Enter a tile URL template');
          const name = layerName || 'XYZ Layer';
          onAddRasterLayer(name, url);
          onClose();
          break;
        }
        case 'wms': {
          if (!url) throw new Error('Enter WMS endpoint URL');
          if (availableLayers.length === 0) {
            const layers = await getWMSLayers(url);
            if (layers.length === 0) throw new Error('No layers found');
            setAvailableLayers(layers);
            setSelectedLayer(layers[0].name);
          } else {
            if (!selectedLayer) throw new Error('Select a layer');
            const tileUrl = buildWMSTileUrl(url, selectedLayer);
            const layer = availableLayers.find(l => l.name === selectedLayer);
            onAddRasterLayer(layerName || layer?.title || selectedLayer, tileUrl);
            onClose();
          }
          break;
        }
        case 'wfs': {
          if (!url) throw new Error('Enter WFS endpoint URL');
          if (availableLayers.length === 0) {
            const types = await getWFSFeatureTypes(url);
            if (types.length === 0) throw new Error('No feature types found');
            setAvailableLayers(types);
            setSelectedLayer(types[0].name);
          } else {
            if (!selectedLayer) throw new Error('Select a feature type');
            const geojson = await fetchWFSFeatures(url, selectedLayer);
            const ft = availableLayers.find(l => l.name === selectedLayer);
            onAddVectorLayer(layerName || ft?.title || selectedLayer, geojson);
            onClose();
          }
          break;
        }
        case 'wmts': {
          if (!url) throw new Error('Enter WMTS endpoint URL');
          if (availableLayers.length === 0) {
            const layers = await getWMTSLayers(url);
            if (layers.length === 0) throw new Error('No layers found');
            setAvailableLayers(layers.map(l => ({ ...l, name: l.identifier })));
            setSelectedLayer(layers[0].identifier);
          } else {
            if (!selectedLayer) throw new Error('Select a layer');
            const layer = availableLayers.find(l => l.identifier === selectedLayer);
            if (!layer?.tileUrl) throw new Error('Layer has no tile URL template');
            onAddRasterLayer(layerName || layer.title || selectedLayer, layer.tileUrl);
            onClose();
          }
          break;
        }
        case 'arcgis': {
          if (!url) throw new Error('Enter ArcGIS service URL');
          if (url.includes('MapServer')) {
            const tileUrl = buildArcGISTileUrl(url);
            onAddRasterLayer(layerName || 'ArcGIS Layer', tileUrl);
            onClose();
          } else {
            const geojson = await fetchArcGISFeatures(url);
            onAddVectorLayer(layerName || 'ArcGIS Features', geojson);
            onClose();
          }
          break;
        }
        case 'osm': {
          if (!overpassQuery) throw new Error('Enter an Overpass query');
          const geojson = await queryOverpass(overpassQuery, getBbox?.());
          onAddVectorLayer(layerName || 'OSM Query', geojson);
          onClose();
          break;
        }
        default:
          throw new Error('Unknown service type');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const activeService = SERVICE_TYPES.find(s => s.id === activeType);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 520, background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text0 }}>Add Service Layer</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        {/* Service type tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 16px', borderBottom: `1px solid ${C.border}`, overflowX: 'auto' }}>
          {SERVICE_TYPES.map(s => (
            <button key={s.id} aria-pressed={activeType === s.id} onClick={() => { setActiveType(s.id); reset(); }}
              style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: activeType === s.id ? C.bg4 : 'transparent', color: activeType === s.id ? C.cyan : C.text2, cursor: 'pointer', fontSize: 11, fontWeight: activeType === s.id ? 600 : 400, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{s.icon}</span> {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 11, color: C.text3 }}>{activeService?.desc}</div>

          {/* Layer name */}
          <div>
            <label style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase' }}>Layer Name</label>
            <input value={layerName} onChange={e => setLayerName(e.target.value)} placeholder="My Layer"
              style={{ width: '100%', fontSize: 12, padding: '7px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' }} />
          </div>

          {/* URL input (not for OSM) */}
          {activeType !== 'osm' && (
            <div>
              <label style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase' }}>
                {activeType === 'xyz' ? 'Tile URL Template (use {z}/{x}/{y})' : 'Service URL'}
              </label>
              <input value={url} onChange={e => { setUrl(e.target.value); reset(); }}
                placeholder={activeType === 'xyz' ? 'https://tile.example.com/{z}/{x}/{y}.png' : 'https://example.com/geoserver/wms'}
                style={{ width: '100%', fontSize: 12, padding: '7px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' }} />
            </div>
          )}

          {/* Overpass query */}
          {activeType === 'osm' && (
            <div>
              <label style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase' }}>Overpass QL Query</label>
              <textarea value={overpassQuery} onChange={e => setOverpassQuery(e.target.value)} rows={4}
                style={{ width: '100%', fontSize: 11, padding: '7px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
              <div style={{ fontSize: 10, color: C.text2, marginTop: 4 }}>
                Examples: <code style={{ color: C.cyan }}>node["amenity"="hospital"](around:50000,27.95,-82.46);</code>
              </div>
            </div>
          )}

          {/* Available layers (for WMS/WFS) */}
          {availableLayers.length > 0 && (
            <div>
              <label style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase' }}>Available Layers</label>
              <select value={selectedLayer} onChange={e => setSelectedLayer(e.target.value)}
                style={{ width: '100%', fontSize: 12, padding: '7px 10px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none' }}>
                {availableLayers.map(l => (
                  <option key={l.name || l.identifier} value={l.name || l.identifier}>
                    {l.title || l.name || l.identifier}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && <div style={{ fontSize: 11, color: C.red, padding: '6px 10px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 }}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg4, color: C.text1, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          <button onClick={handleConnect} disabled={loading}
            style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', fontWeight: 600, cursor: loading ? 'wait' : 'pointer', fontSize: 12, opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Connecting...' : availableLayers.length > 0 ? 'Add Layer' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
}
